import type { Workbook } from 'exceljs';
import * as momentNs from 'moment-timezone';
import objectPath from 'object-path';
import {
  EMPTY,
  expand,
  firstValueFrom,
  Observable,
  reduce,
  Subject,
  takeUntil,
} from 'rxjs';
import { NgrxLoaderFacade } from '@ellf/app-ngrx-loader';
import { ToastService } from '@ellf/app-ui-kit';
import { BaseDbDto } from '@ellf/common-dto';
import {
  FieldConfig,
  FieldType,
  FilterValue,
  PagedResultDto,
} from '@ellf/common-interfaces';
import { sanitizeFileName } from '@ellf/common-utils';
import { Action } from '@ngrx/store';
import { mapFromColumnState, mapFromFilterModel } from './ag-grid-utils';
import { EntityListField } from './entity-list-field';
import { FacadeInterface } from './facade.interface';

export interface ExcelFieldConfig extends FieldConfig {
  valueGetter?: (data: any) => any;
}
export async function createExcel<T>(
  data: T[],
  fileName: string,
  columns: ExcelFieldConfig[],
  beforeAddWorksheet?: (workbook: Workbook) => void,
  afterAddWorksheet?: (workbook: Workbook) => void,
): Promise<ArrayBuffer> {
  // exceljs pesa ~1.5 MB: viene caricato solo quando serve esportare
  const { Workbook } = await import('exceljs');
  const workbook = new Workbook(); // Create a new workbook

  if (beforeAddWorksheet) {
    beforeAddWorksheet(workbook);
  }
  addWorkbook(workbook, data, fileName, columns);
  if (afterAddWorksheet) {
    afterAddWorksheet(workbook);
  }
  return workbook.xlsx.writeBuffer();
}
export function addWorkbook<T>(
  workbook: Workbook,
  data: T[],
  fileName: string,
  columns: ExcelFieldConfig[],
) {
  // Excel limita il nome del foglio a 31 caratteri
  const worksheet = workbook.addWorksheet(sanitizeFileName(fileName).slice(0, 31)); // New Worksheet

  const columnList = columns.map((c: FieldConfig) => {
    let style: any = undefined;
    if (c.type === FieldType.date) {
      style = { numFmt: 'dd/mm/yyyy' };
    }
    return {
      header: c.label,
      key: c.name,
      width: 20,
      style,
    };
  });

  worksheet.columns = columnList;
  // header column
  worksheet.getRow(1).eachCell((cell: any) => {
    cell.font = {
      bold: true,
    };
    cell.fill = {
      type: 'pattern',
      pattern: 'gray0625',
    };
  });

  // Looping through data

  data.forEach((item: T) => {
    const row: any = {};
    columns.map((cell: ExcelFieldConfig) => {
      row[cell.name] = getRowValue(row, item, cell);
    });
    // Add data in worksheet
    worksheet.addRow(row);
  });

  // add border in cells
  // NB: non lasciare celle vuote, altrimenti non mette il bordo
  worksheet.eachRow((row: any) => {
    row.eachCell((cell: any) => {
      cell.border = {
        top: { style: 'hair' },
        left: { style: 'hair' },
        bottom: { style: 'hair' },
        right: { style: 'hair' },
      };
    });
  });

  // salvo il file su server solo per verifica
  // workbook.xlsx.writeFile(`${this.excelPath}/${request.body.fileName}.xlsx`);
}

function getRowValue(row: any, item: any, cell: ExcelFieldConfig): any {
  let value = objectPath.get(item, cell.name);
  if (cell.valueGetter) {
    value = cell.valueGetter({ data: item });
  }
  const moment = momentNs;
  const integerFormatter = new Intl.NumberFormat('it-IT', {
    maximumFractionDigits: 0,
  });
  const decimalFormatter = new Intl.NumberFormat('it-IT', {
    style: 'decimal',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  const percentFormatter = new Intl.NumberFormat('it-IT', {
    style: 'decimal',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  const currencyFormatter = new Intl.NumberFormat('it-IT', {
    style: 'currency',
    currency: 'EUR',
  });
  switch (cell.type) {
    case FieldType.date:
      value =
        value != null
          ? moment.tz(value, 'Europe/Rome').format('DD/MM/YYYY')
          : '';
      break;
    case FieldType.time:
      value =
        value != null ? moment.tz(value, 'Europe/Rome').format('HH:mm:ss') : '';
      break;
    case FieldType.datetime:
      value =
        value != null
          ? moment.tz(value, 'Europe/Rome').format('DD/MM/YYYY HH:mm:ss')
          : '';
      break;

    case FieldType.integer:
      value =
        value != undefined && value >= 0 ? integerFormatter.format(value) : '';
      break;
    case FieldType.decimal:
      value =
        value != undefined && value >= 0 ? decimalFormatter.format(value) : '';
      break;
    case FieldType.percent:
      value =
        value != undefined && value >= 0
          ? percentFormatter.format(value) + '%'
          : '';
      break;
    // case FieldType.percent: // NB: il valore deve essere compreso fra 0 e 1, in db con 4 decimali per avere la precisione 2 (es 13,45%)
    //   const percentFormatter = new Intl.NumberFormat('it-IT', {
    //     style: 'percent',
    //     minimumFractionDigits: 2,
    //     maximumFractionDigits: 2,
    //   });
    //   value =
    //     value != undefined && value >= 0
    //       ? percentFormatter.format(value)
    //       : '';
    // break;
    case FieldType.currency:
      value =
        value != undefined && value >= 0 ? currencyFormatter.format(value) : '';
      break;
    case FieldType.boolean:
      value = value == true ? 'Sì' : value == false ? 'No' : '';
      break;
    case FieldType.enum:
      value =
        cell.elementValues && cell.elementValues.length
          ? cell.elementValues.find((v) => v.value === value)?.description // value potrebbe essere una stringa e non si può utilizzare cell.elementValues[value]
          : value;
      break;
    case FieldType.json:
      value =
        value != null
          ? JSON.stringify(value)
              .replace('{', '')
              .replace('}', '')
              .split(',')
              .join('\n')
              .split(':')
              .join(': ')
              .split('\\')
              .join('')
              .split('"')
              .join('')
          : '';
      break;
    case FieldType.text:
    case FieldType.year:
    case FieldType.integerNoThousandSeparator:
      break;
  }

  if (value == undefined) {
    value = '';
  }

  return value;
}
export function downloadXlsx(data: ArrayBuffer, fileName: string) {
  const blob = new Blob([data], {
    type: 'application/xlsx',
  });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `${sanitizeFileName(fileName)}.xlsx`;
  document.body.appendChild(link);
  link.click();

  URL.revokeObjectURL(url);
}
export async function downloadExcelData<T extends BaseDbDto>(
  action: Action,
  loaderFacade: NgrxLoaderFacade,
  facade: FacadeInterface<T>,
  ngUnsubscribe: Subject<void>,
  toastService: ToastService,
  excelColumns: FieldConfig[],
  exportJoin: string[] | undefined,
  fileName: string,
  getPaged: (
    take: number,
    skip: number,
    orderBy?: { [field: string]: 'asc' | 'desc' },
    filters?: FilterValue[],
    join?: string[],
  ) => Observable<PagedResultDto<T>>,
  processingMessage = 'processati ${count} record su ${total}',
  take = 200,
  startMessage = 'operazione in corso...',
  fields?: EntityListField[],
) {
  const begin = new Date().getTime();
  loaderFacade.showLoader(action);

  const filterModel = await firstValueFrom(facade.getFilterModel$);
  const filters = mapFromFilterModel(filterModel, fields);
  const columnState = await firstValueFrom(facade.getColumnState$);
  const orderBy = mapFromColumnState(columnState);

  let totalCount = -1;
  let skip = 0;
  loaderFacade.updateLoaderText(startMessage);
  getPaged(take, skip, orderBy, filters, exportJoin)
    .pipe(
      expand((res) => {
        if (skip === 0) {
          totalCount = res.totalCount;
        }
        if (totalCount > take + skip) {
          loaderFacade.updateLoaderText(
            processingMessageParse(
              processingMessage,
              (skip + take).toString(),
              totalCount.toString(),
            ),
          );
          skip += take;
          return getPaged(take, skip, orderBy, filters, exportJoin);
        } else {
          return EMPTY;
        }
      }),
      reduce((acc, res) => {
        return [...acc, ...res.items];
      }, [] as T[]),
      takeUntil(ngUnsubscribe),
    )
    .subscribe({
      next: (results) => {
        loaderFacade.updateLoaderText(
          processingMessageParse(
            processingMessage,
            results.length.toString(),
            totalCount.toString(),
          ),
        );
        createExcel(results, fileName, excelColumns || []).then(
          (data) => {
            downloadXlsx(data, fileName);
            const end = new Date().getTime();
            // eslint-disable-next-line no-console
            console.log(`tempo impiegato con ${take}: `, end - begin);
            toastService.successFeedback(
              'Download Excel avvenuto con successo!',
            );
          },
          (e) => {
            loaderFacade.hideLoader(action);
            toastService.errorFeedback(
              'Si è verificato un errore durante il download!' + '(' + e + ')',
            );
          },
        );
      },
      error: () => {
        loaderFacade.hideLoader(action);
        toastService.errorFeedback(
          'Si è verificato un errore durante il download!',
        );
      },
      complete: () => loaderFacade.hideLoader(action),
    });
}

function processingMessageParse(
  processingMessage: string,
  countParam: string,
  totalparam: string,
) {
  return processingMessage
    .replace('${count}', countParam)
    .replace('${total}', totalparam);
}
